import React, { useCallback, memo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Course } from '../../types/course';
import { BASE_URL } from '../../apiConfig';
import EnrollmentPopup from './EnrollmentPopup';
import { 
  BookOpen, 
  Clock, 
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Play
} from 'lucide-react';

interface CourseCardProps {
  course: Course;
  isActive?: boolean;
  isPartial?: boolean;
  onClick?: () => void;
  position?: 'left' | 'center' | 'right';
  isEnrolled?: boolean;
}

// Memoized price formatter
const formatPrice = (price: number): string => {
  if (price === 0) return '0';
  return price.toLocaleString('ar-EG');
};

// Memoized term translator
const getTermInArabic = (term: string): string => {
  const terms: Record<string, string> = {
    'First': 'الترم الأول',
    'Second': 'الترم الثاني',
    'Summer': 'الفصل الصيفي'
  };
  return terms[term] || term;
};

// Grade translator function
const getGradeInArabic = (grade: string): string => {
  const gradeTranslations: Record<string, string> = {
    'Secondary1': 'أولى ثانوي',
    'Secondary2': 'ثانية ثانوي',
    'Secondary3': 'ثالثة ثانوي',
    'default': grade
  };
  return gradeTranslations[grade] || grade;
};

// Memoized course image source getter
const getCourseImageSrc = (course: Course): string => {
  if (!course.imagePath) {
    return '/default-course.jpg';
  }
  
  if (course.imagePath.startsWith('http')) {
    return course.imagePath;
  }
  
  let imagePath = course.imagePath;
  if (imagePath.startsWith('/')) {
    imagePath = imagePath.substring(1);
  }
  
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
  return `${baseUrl}${imagePath}`;
};

// Grade color function
const getGradeColor = (grade: string): string => {
  const colors: Record<string, string> = {
    'First': 'bg-blue-100 text-blue-800',
    'Second': 'bg-purple-100 text-purple-800',
    'Summer': 'bg-green-100 text-green-800',
    'default': 'bg-gray-100 text-gray-800'
  };
  return colors[grade] || colors['default'];
};

const CourseCard: React.FC<CourseCardProps> = ({ 
  course, 
  isActive = true, 
  isPartial = false, 
  onClick, 
  position = 'center',
  isEnrolled = false
}) => {
  const formattedPrice = formatPrice(course.price ?? 0);
  const [showEnrollmentPopup, setShowEnrollmentPopup] = useState(false);
  
  // Memoized error handler
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/default-course.jpg';
  }, []);

  const handleEnrollClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEnrollmentPopup(true);
  };

  const cardClasses = `bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 ${
    isActive 
      ? 'scale-100 z-20 opacity-100' 
      : isPartial 
      ? 'scale-90 z-10 opacity-70 hover:opacity-90 hover:scale-95' 
      : 'scale-75 z-0 opacity-50'
  } ${position === 'left' ? '-translate-x-4' : position === 'right' ? 'translate-x-4' : ''}`;

  return (
    <>
      <div 
        className={`${cardClasses} ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
      >
      {/* Top Banner Section */}
      <div className="relative aspect-[1280/718] bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-16 h-16 bg-white/20 rounded-full"></div>
          <div className="absolute top-8 right-8 w-12 h-12 bg-white/20 rounded-full"></div>
          <div className="absolute bottom-4 left-8 w-20 h-20 bg-white/20 rounded-full"></div>
        </div>
        
        {/* Instructor Image */}
        <div className="absolute inset-0">
          <img
            src={getCourseImageSrc(course)}
            alt={course.courseName}
            className="w-full h-full object-contain object-center"
            onError={handleImageError}
            loading="lazy"
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>
        
        {/* Free Badge */}
        {course.price === 0 && (
          <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            مجاناً
          </div>
        )}
        

      </div>
      
      {/* Bottom White Card Section */}
      <div className="p-4 relative -mt-10 bg-white rounded-t-xl shadow-lg border border-gray-100 mx-4">
        {/* Main Course Title */}
        <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
          {course.courseName}
        </h3>
        
        {/* Course Description */}
        {course.description && (
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600 line-clamp-2">
              {course.description}
            </p>
            {course.description.length > 100 && (
              <button 
                className="text-blue-600 hover:text-blue-700 text-xs font-medium mt-2 hover:underline transition-colors duration-200"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const descElement = e.currentTarget.previousElementSibling;
                  if (descElement) {
                    descElement.classList.toggle('line-clamp-2');
                    e.currentTarget.textContent = descElement.classList.contains('line-clamp-2') ? 'عرض المزيد' : 'عرض أقل';
                  }
                }}
              >
                عرض المزيد
              </button>
            )}
            <div className="w-16 h-0.5 bg-blue-400 mx-auto mt-2"></div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="space-y-3 mb-4">
          {/* Enter Course Button */}
          <Link
            to={`/courses/${course.id}`}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-1.5 px-3 rounded-full shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1.5 group"
          >
            <Play className="w-3.5 h-3.5 group-hover:scale-110 transition-transform duration-300" />
            <span className="text-sm">الدخول للكورس</span>
            <div className="w-1 h-1 bg-white rounded-full group-hover:animate-pulse"></div>
          </Link>
          
          {/* Enrollment Button */}
          {isEnrolled ? (
            <Link
              to={`/course-player/${course.id}`}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-200 hover:shadow-lg text-center block"
            >
              استمرار
            </Link>
          ) : (
            <button 
              onClick={handleEnrollClick}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-200 hover:shadow-lg"
            >
              اشترك الآن
            </button>
          )}
        </div>
        
        {/* Course Details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-xs text-gray-500">
            <Clock className="w-3 h-3 mr-1.5 text-blue-500" />
            <span>الترم: {getTermInArabic(course.term || 'First')}</span>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <GraduationCap className="w-3 h-3 mr-1.5 text-purple-500" />
            <span>الصف: {getGradeInArabic(course.grade || 'Secondary1')}</span>
          </div>
        </div>
        
        {/* Price Display */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-gray-100">
            <span className="text-xs text-gray-500 font-medium">السعر:</span>
            <span className="text-xl font-bold text-gray-900">
              {course.price === 0 ? 'مجاناً' : formattedPrice}
            </span>
            {course.price !== 0 && (
              <span className="text-green-500 font-medium text-sm">جنيه</span>
            )}
          </div>
        </div>
        
        {/* Free Course Badge */}
        {course.price === 0 && (
          <div className="text-center">
            <span className="inline-block bg-pink-500 text-white font-semibold py-1 px-3 rounded-lg text-sm">
              كورس مجاني
            </span>
          </div>
        )}
      </div>
      </div>
      
      {/* Enrollment Popup */}
      <EnrollmentPopup
        isOpen={showEnrollmentPopup}
        onClose={() => setShowEnrollmentPopup(false)}
        courseId={course.id}
      />
    </>
  );
};

// CourseCarousel Component
interface CourseCarouselProps {
  courses: Course[];
  title?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  itemsPerView?: number;
  className?: string;
}

const CourseCarousel: React.FC<CourseCarouselProps> = ({
  courses,
  title = 'الدورات المتاحة',
  autoPlay = false,
  autoPlayInterval = 5000,
  showDots = true,
  itemsPerView = 3,
  className = ''
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Calculate total slides based on items per view
  const totalSlides = Math.max(0, courses.length - itemsPerView + 1);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && !isHovered && courses.length > itemsPerView) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex >= totalSlides - 1 ? 0 : prevIndex + 1
        );
      }, autoPlayInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoPlay, isHovered, totalSlides, autoPlayInterval, courses.length, itemsPerView]);

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex <= 0 ? totalSlides - 1 : prevIndex - 1
    );
  }, [totalSlides]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex >= totalSlides - 1 ? 0 : prevIndex + 1
    );
  }, [totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, totalSlides - 1)));
  }, [totalSlides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    if (carouselRef.current) {
      carouselRef.current.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      if (carouselRef.current) {
        carouselRef.current.removeEventListener('keydown', handleKeyPress);
      }
    };
  }, [goToPrevious, goToNext]);

  // Handle course card click for inactive cards
  const handleCourseClick = useCallback((courseIndex: number) => {
    const slideIndex = Math.max(0, courseIndex - Math.floor(itemsPerView / 2));
    setCurrentIndex(Math.min(slideIndex, totalSlides - 1));
  }, [itemsPerView, totalSlides]);

  // Get position for a course card
  const getCardPosition = (index: number): 'left' | 'center' | 'right' | 'hidden' => {
    const relativeIndex = index - currentIndex;
    if (relativeIndex < 0 || relativeIndex >= itemsPerView) return 'hidden';
    if (itemsPerView === 1) return 'center';
    if (itemsPerView === 2) return relativeIndex === 0 ? 'left' : 'right';
    if (relativeIndex === 0) return 'left';
    if (relativeIndex === itemsPerView - 1) return 'right';
    return 'center';
  };

  // Check if a course is active (in the center for odd itemsPerView, or left-center for even)
  const isCardActive = (index: number): boolean => {
    const relativeIndex = index - currentIndex;
    const centerIndex = Math.floor(itemsPerView / 2);
    return relativeIndex === centerIndex;
  };

  // Check if a course is partially visible
  const isCardPartial = (index: number): boolean => {
    const relativeIndex = index - currentIndex;
    return relativeIndex >= 0 && relativeIndex < itemsPerView && !isCardActive(index);
  };

  if (!courses.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">لا توجد دورات متاحة حالياً</p>
      </div>
    );
  }

  // If courses fit in view, show them all without carousel
  if (courses.length <= itemsPerView) {
    return (
      <div className={`w-full ${className}`}>
        {title && (
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
          </div>
        )}
        <div className={`grid gap-6 ${
          courses.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
          courses.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'
        }`}>
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              isActive={true}
              isPartial={false}
              isEnrolled={false}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={carouselRef}
      className={`w-full relative ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="region"
      aria-label="دوّار الدورات"
    >
      {/* Title */}
      {title && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{title}</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </div>
      )}

      {/* Main Carousel Container */}
      <div className="relative overflow-hidden px-4 md:px-8">
        {/* Course Cards Container */}
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
            width: `${(courses.length * 100) / itemsPerView}%`
          }}
        >
          {courses.map((course, index) => {
            const position = getCardPosition(index);
            if (position === 'hidden') {
              return (
                <div 
                  key={course.id} 
                  className="flex-shrink-0"
                  style={{ width: `${100 / courses.length}%` }}
                />
              );
            }

            return (
              <div 
                key={course.id}
                className="flex-shrink-0 px-3"
                style={{ width: `${100 / courses.length}%` }}
              >
                <CourseCard
                  course={course}
                  isActive={isCardActive(index)}
                  isPartial={isCardPartial(index)}
                  onClick={() => handleCourseClick(index)}
                  position={position}
                  isEnrolled={false}
                />
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 rounded-full p-1.5 shadow-lg transition-all duration-200 hover:scale-110 z-30"
          aria-label="الدورة السابقة"
          disabled={totalSlides <= 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        
        <button
          onClick={goToNext}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-700 rounded-full p-1.5 shadow-lg transition-all duration-200 hover:scale-110 z-30"
          aria-label="الدورة التالية"
          disabled={totalSlides <= 1}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Dots Pagination */}
      {showDots && totalSlides > 1 && (
        <div className="flex justify-center mt-6 space-x-1.5">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                currentIndex === index
                  ? 'bg-blue-600 scale-110'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`انتقل إلى الشريحة ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Course Counter */}
      <div className="text-center mt-3 text-xs text-gray-500">
        <span>
          {Math.min(currentIndex + itemsPerView, courses.length)} من {courses.length} دورة
        </span>
      </div>
    </div>
  );
};

export default memo(CourseCard); 